import { ReactNode } from 'react';

import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import PageLayout from '~layouts/page/pageLayout';
import Dimens from '~themes/dimens';

import WikiToc from './components/WikiToc';

interface WikiLayoutProps {
	children?: ReactNode;
}

/**
 * Wiki layout: main content + side Table of Contents.
 *
 * Notes:
 * - We use CSS `position: sticky` (no ResizeObserver / JS measurement).
 * - Wiki-only “cover/spacer” lives here (not in `PageLayout`).
 */
const WikiLayout = ({ children }: WikiLayoutProps) => {
	const { t } = useTranslation('nav');

	const coverHeight = {
		xs: Dimens.spacingMd,
		md: Dimens.spacingMd,
		lg: Dimens.spacing4xl,
	} as const;

	return (
		<PageLayout
			pageTitle={t('wiki')}
			headerInfo={{ title: t('wiki') }}
			hideBreadcrumbs
			disableContentPadding
			disableContentScroll
		>
			<Box
				sx={{
					height: 1,
					minHeight: 0,
					display: 'flex',
					flexDirection: 'column',
					px: {
						xs: Dimens.spacingMd,
						md: Dimens.spacing4xl,
					},
				}}
			>
				{/* Fixed cover/spacer so columns start aligned under the header */}
				<Box
					sx={(theme) => ({
						height: coverHeight,
						flexShrink: 0,
						backgroundColor: theme.palette.bgSecondary.main,
						pointerEvents: 'none',
					})}
				/>

				<Box
					sx={{
						flex: '1 1 auto',
						minHeight: 0,
						minWidth: 320,
						display: { xs: 'flex', lg: 'grid' },
						flexDirection: { xs: 'column', lg: undefined },
						gap: { xs: Dimens.spacingXl, lg: Dimens.spacingNone },
						gridTemplateColumns: { lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
						columnGap: { lg: Dimens.spacing3xl },
					}}
				>
					{/* Content column (scrolls independently) */}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							minWidth: 0,
							minHeight: 0,
							overflowY: 'auto',
							overflowX: 'hidden',
							scrollbarWidth: 'none', // Firefox
							'&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari/Edge
							msOverflowStyle: 'none',
							pb: {
								xs: Dimens.spacingMd,
								md: Dimens.spacing4xl,
							},
						}}
					>
						{/* On small screens the ToC is rendered above the content (single scroll area) */}
						<Box sx={{ display: { xs: 'block', lg: 'none' }, mb: Dimens.spacingXl }}>
							<WikiToc />
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
							{children ?? <Outlet />}
						</Box>
					</Box>

					{/* ToC column (independent scroll, only on large screens) */}
					<Box
						sx={{
							display: { xs: 'none', lg: 'block' },
							minWidth: 0,
							minHeight: 0,
							overflowY: 'auto',
							overflowX: 'hidden',
							overscrollBehavior: 'contain',
							pb: Dimens.spacing4xl,

							scrollbarWidth: 'none', // Firefox
							'&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari/Edge
							msOverflowStyle: 'none',
						}}
					>
						<WikiToc />
					</Box>
				</Box>
			</Box>
		</PageLayout>
	);
};

export default WikiLayout;
